import type { TrainingEvalEpisodeView } from "@/api/trainingEval";
import { episodeOutcomeMeta, isSuccessOutcome } from "./evalRunResultModel";

/**
 * Owns: Episode 시간의 표시 전용 분류와 통계. 0은 측정값, -1은 reset 측정 불가,
 * null·누락은 미기록이다. 그 밖의 비정상 값도 통계에서 제외한다.
 * Does not own: API 응답 변경, 결과 판정, 영상 시간과의 연결.
 */

export type EpisodeTime = {
  index: number;
  envId: number | null;
  episodeIndex: number | null;
  outcome: string;
  seconds: number | null;
  reason: "after_reset" | "missing" | "invalid" | null;
};

export function episodeTimeRows(episodes: readonly TrainingEvalEpisodeView[]): EpisodeTime[] {
  return episodes
    .map((episode): EpisodeTime => {
      const value = episode.elapsed_s;
      const measured = typeof value === "number" && Number.isFinite(value) && value >= 0;
      return {
        index: episode.index,
        envId: episode.env_id,
        episodeIndex: episode.episode_index,
        outcome: episodeOutcomeMeta(episode.outcome).label,
        seconds: measured ? value : null,
        reason: measured
          ? null
          : value === -1
            ? "after_reset"
            : value == null
              ? "missing"
              : "invalid",
      };
    })
    .sort(
      (left, right) =>
        (left.envId ?? Infinity) - (right.envId ?? Infinity) ||
        (left.episodeIndex ?? Infinity) - (right.episodeIndex ?? Infinity) ||
        left.index - right.index,
    );
}

export function summarizeEpisodeTimes(rows: readonly EpisodeTime[]) {
  let measured = 0;
  let sum = 0;
  let min: number | null = null;
  let max: number | null = null;
  let afterReset = 0;
  let missing = 0;
  let invalid = 0;
  for (const row of rows) {
    if (row.seconds !== null) {
      measured += 1;
      sum += row.seconds;
      min = min === null ? row.seconds : Math.min(min, row.seconds);
      max = max === null ? row.seconds : Math.max(max, row.seconds);
    } else if (row.reason === "after_reset") {
      afterReset += 1;
    } else if (row.reason === "missing") {
      missing += 1;
    } else {
      invalid += 1;
    }
  }
  return {
    total: rows.length,
    measured,
    afterReset,
    missing,
    invalid,
    mean: measured === 0 ? null : sum / measured,
    min,
    max,
  };
}

export function formatEpisodeSeconds(seconds: number | null): string {
  return seconds === null ? "—" : seconds.toFixed(2);
}

export function successfulEpisodeTime(episodes: readonly TrainingEvalEpisodeView[]) {
  return summarizeEpisodeTimes(
    episodeTimeRows(episodes.filter((episode) => isSuccessOutcome(episode.outcome))),
  );
}
